/** The page a share link opens.
 *
 * Rendered **outside** the authentication gate: the whole point is that the reader has no
 * account. It therefore assumes nothing about the viewer — no navigation rail, no theme
 * preference stored against a user, no jargon that only makes sense inside the team.
 *
 * It also never explains why a link failed. Unknown, expired and revoked all read the
 * same, because telling a stranger which one it was is telling them something about the
 * links that do exist.
 *
 * The first version was a name, a health pill and a progress bar. Brian's assessment was
 * that one bar was not going to cut it, and he was right: a percentage with nothing beside
 * it invites the reader to ask every question the page should have answered. This version
 * reads as a status report — schedule against progress, what is next, what it is waiting
 * on, who is on it, and where the paperwork stands.
 *
 * Two things it deliberately does *not* do:
 *
 * * No interactive controls. Nothing here writes, so there is nothing to submit, and a
 *   page with no form cannot be turned into one by whoever ends up holding the URL.
 * * No text the reader cannot act on. Every empty section says what its absence means
 *   rather than disappearing, because a section that vanishes reads as a page that forgot.
 */

import { useParams } from 'react-router-dom'

import { Loading, Pill } from '@/components/ui'
import { formatDate, ragTone, stageLabel, statusLabel } from '@/lib/domain'
import {
  useSharedPage,
  type SharedDependency,
  type SharedProject,
  type SharedStatus,
  type SharedWorkItem,
} from '@/features/shares/useShares'

/** RAG values reach us as domain strings; `none` has no status colour of its own. */
function ragVar(rag: string): string {
  return `var(--status-${rag === 'none' ? 'info' : rag})`
}

function Frame({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-full bg-canvas px-6 py-10">
      <div className="mx-auto max-w-[920px]">
        <div className="mb-8 flex items-center gap-3">
          <div
            className="flex h-9 w-9 items-center justify-center rounded-lg font-heading text-xl font-bold"
            style={{ backgroundColor: 'var(--brand)', color: 'var(--brand-on)' }}
          >
            W
          </div>
          <div>
            <div className="font-heading text-sm font-bold uppercase text-fg">Wheeler PMT</div>
            <div className="micro-label text-fg-muted">IT Operations</div>
          </div>
        </div>
        {children}
        <p className="mt-10 border-t border-border pt-4 text-xs text-fg-muted">
          A read-only view shared from Wheeler PMT. Figures update when the project does, so
          this page is current every time it is opened.
        </p>
      </div>
    </div>
  )
}

/** Section heading: the design system's short vertical yellow rule plus a micro-label. */
function Section({
  title,
  note,
  children,
}: {
  title: string
  note?: string
  children: React.ReactNode
}) {
  return (
    <section className="mt-8">
      <div className="flex items-baseline gap-2">
        <span
          className="inline-block h-4 w-[3px] shrink-0 rounded-sm"
          style={{ backgroundColor: 'var(--brand)' }}
          aria-hidden
        />
        <h2 className="micro-label text-fg">{title}</h2>
        {note ? <span className="text-xs text-fg-muted">{note}</span> : null}
      </div>
      <div className="mt-3">{children}</div>
    </section>
  )
}

function Figure({
  value,
  label,
  tone,
  hint,
}: {
  value: string
  label: string
  tone?: string
  hint?: string
}) {
  return (
    <div
      className="card min-w-[128px] flex-1 px-4 py-3"
      style={tone ? { borderLeft: `3px solid ${tone}` } : undefined}
    >
      <div className="font-heading text-[30px] leading-none text-fg">{value}</div>
      <div className="micro-label mt-1 text-fg-muted">{label}</div>
      {hint ? <div className="mt-1 text-xs text-fg-muted">{hint}</div> : null}
    </div>
  )
}

/** A labelled bar. Used twice, which is the point — see `ProgressAgainstTime`. */
function Bar({ percent, color }: { percent: number; color: string }) {
  return (
    <div className="h-2.5 w-full overflow-hidden rounded-pill bg-canvas">
      <div
        className="h-full rounded-pill"
        style={{ width: `${Math.min(100, Math.max(0, percent))}%`, backgroundColor: color }}
      />
    </div>
  )
}

/**
 * Delivery progress and elapsed schedule, on the same scale.
 *
 * This is the one comparison a status page exists to make. 40% built with 90% of the
 * window gone is the situation a reader needs to see without being told, and neither
 * number alone shows it. Stacking the bars makes the gap the visual — no commentary
 * required, and none offered: the page states the two figures and lets the reader draw the
 * conclusion, which is also the honest thing to do when the schedule data may be thin.
 */
function ProgressAgainstTime({ page }: { page: SharedProject }) {
  const timeUsed = page.schedule.timeUsedPercent

  return (
    <div className="card p-5">
      <div className="flex items-end justify-between gap-4">
        <span className="micro-label text-fg-muted">Work complete</span>
        <span className="font-heading text-3xl leading-none text-fg">{page.percentComplete}%</span>
      </div>
      <div className="mt-2">
        <Bar percent={page.percentComplete} color={ragVar(page.ragStatus)} />
      </div>

      {timeUsed === null ? (
        <p className="mt-4 text-xs text-fg-muted">
          No start and target date are set, so elapsed time cannot be shown against progress.
        </p>
      ) : (
        <>
          <div className="mt-5 flex items-end justify-between gap-4">
            <span className="micro-label text-fg-muted">Schedule elapsed</span>
            <span className="font-heading text-xl leading-none text-fg-muted">{timeUsed}%</span>
          </div>
          <div className="mt-2">
            <Bar percent={timeUsed} color="var(--fg-muted)" />
          </div>
          <p className="mt-3 text-xs text-fg-muted">
            {page.schedule.durationDays} day window, {page.schedule.daysElapsed} days in.
          </p>
        </>
      )}
    </div>
  )
}

function MilestoneList({ page }: { page: SharedProject }) {
  if (page.milestones.length === 0) {
    return <p className="text-sm text-fg-muted">No milestones have been set for this project.</p>
  }

  const complete = page.milestones.filter((m) => m.isComplete).length

  return (
    <>
      <ul className="card divide-y divide-border">
        {page.milestones.map((milestone) => (
          <li
            key={`${milestone.title}-${milestone.dueDate ?? 'none'}`}
            className="flex flex-wrap items-center gap-3 px-4 py-2.5"
          >
            <span
              className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-[10px] font-bold"
              style={{
                backgroundColor: milestone.isComplete
                  ? 'var(--status-green)'
                  : milestone.isOverdue
                    ? 'var(--status-red)'
                    : 'var(--border)',
                color: milestone.isComplete || milestone.isOverdue ? '#fff' : 'var(--fg-muted)',
              }}
              aria-hidden
            >
              {milestone.isComplete ? '✓' : ''}
            </span>
            <span
              className={`min-w-0 flex-1 text-sm ${
                milestone.isComplete ? 'text-fg-muted line-through' : 'text-fg'
              }`}
            >
              {milestone.title}
            </span>
            {milestone.isOverdue ? <Pill tone="red">Past due</Pill> : null}
            <span className="w-28 text-right text-sm text-fg-muted">
              {formatDate(milestone.dueDate)}
            </span>
          </li>
        ))}
      </ul>
      <p className="mt-2 text-xs text-fg-muted">
        {complete} of {page.milestones.length} complete.
      </p>
    </>
  )
}

const ITEM_STATUS: Record<SharedWorkItem['status'], { label: string; tone: 'red' | 'info' | 'neutral' | 'green' }> = {
  blocked: { label: 'Blocked', tone: 'red' },
  in_progress: { label: 'In progress', tone: 'info' },
  todo: { label: 'To do', tone: 'neutral' },
  done: { label: 'Done', tone: 'green' },
}

/**
 * The work itself — what is being done and by whom (Brian, 2026-09-30). Open items first,
 * then what finished recently, so a reader sees movement as well as what is left.
 */
function WorkItems({ page }: { page: SharedProject }) {
  const { items, itemsOmitted, recentlyDoneDays } = page.delivery
  if (items.length === 0) return null
  const open = items.filter((item) => item.status !== 'done')
  const done = items.filter((item) => item.status === 'done')

  const row = (item: SharedWorkItem, index: number) => {
    const status = ITEM_STATUS[item.status]
    return (
      <li key={`${item.status}-${index}-${item.title}`} className="px-4 py-3">
        <div className="flex flex-wrap items-center gap-2">
          <Pill tone={status.tone}>{status.label}</Pill>
          <span className="min-w-0 flex-1 font-medium text-fg">{item.title}</span>
          <span className="text-xs text-fg-muted">
            {item.assignee ?? 'Not yet assigned'}
            {item.completedOn ? ` · finished ${formatDate(item.completedOn)}` : ''}
          </span>
        </div>
        {item.description ? (
          <p className="mt-1.5 whitespace-pre-wrap text-sm text-fg-muted">{item.description}</p>
        ) : null}
      </li>
    )
  }

  return (
    <div className="space-y-4">
      {open.length ? (
        <div>
          <p className="micro-label mb-2 text-fg-muted">Being worked on</p>
          <ul className="card divide-y divide-border">{open.map(row)}</ul>
        </div>
      ) : null}
      {done.length ? (
        <div>
          <p className="micro-label mb-2 text-fg-muted">Finished in the last {recentlyDoneDays} days</p>
          <ul className="card divide-y divide-border">{done.map(row)}</ul>
        </div>
      ) : null}
      {itemsOmitted > 0 ? (
        <p className="text-xs text-fg-muted">And {itemsOmitted} more not shown.</p>
      ) : null}
    </div>
  )
}

function Delivery({ page }: { page: SharedProject }) {
  const { delivery } = page

  if (delivery.total === 0) {
    return (
      <p className="text-sm text-fg-muted">
        No individual work items are tracked for this project. Progress is reported at the
        project level.
      </p>
    )
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-3">
        <Figure value={String(delivery.done)} label="Done" tone="var(--status-green)" />
        <Figure
          value={String(delivery.inProgress)}
          label="In progress"
          tone="var(--status-info)"
        />
        {delivery.blocked > 0 ? (
          <Figure value={String(delivery.blocked)} label="Blocked" tone="var(--status-red)" />
        ) : null}
        <Figure
          value={String(delivery.total)}
          label="Total items"
          hint={
            delivery.pointsTotal
              ? `${delivery.pointsDone ?? 0} of ${delivery.pointsTotal} points`
              : undefined
          }
        />
      </div>

      {delivery.currentSprint ? (
        <div className="card p-4">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <span className="font-heading text-base text-fg">{delivery.currentSprint.name}</span>
            <span className="text-xs text-fg-muted">
              {formatDate(delivery.currentSprint.startDate)} –{' '}
              {formatDate(delivery.currentSprint.endDate)} · {delivery.currentSprint.daysLeft} days
              left
            </span>
          </div>
          {delivery.currentSprint.goal ? (
            <p className="mt-1 text-sm text-fg-muted">{delivery.currentSprint.goal}</p>
          ) : null}
          <p className="mt-2 text-sm text-fg">
            {delivery.currentSprint.itemsDone} of {delivery.currentSprint.items} items done in
            this sprint.
          </p>
        </div>
      ) : null}

      <WorkItems page={page} />
    </div>
  )
}

/**
 * Long-running work counted out of a spreadsheet.
 *
 * `syncedHoursAgo` is shown rather than quietly dropped. A confident "412 of 500" that is
 * four days stale is worse than no number, and that failure happened in production for
 * seventeen hours while every health check stayed green.
 */
function Trackers({ page }: { page: SharedProject }) {
  if (page.trackers.length === 0) return null

  return (
    <Section title="Long-running work" note="counted from the source spreadsheet">
      <ul className="card divide-y divide-border">
        {page.trackers.map((tracker) => {
          const stale = tracker.syncedHoursAgo !== null && tracker.syncedHoursAgo > 6
          return (
            <li key={tracker.title} className="px-4 py-3">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <span className="text-sm text-fg">{tracker.title}</span>
                <span className="text-sm text-fg-muted">
                  {tracker.total === null || tracker.done === null
                    ? 'Not yet counted'
                    : `${tracker.done} of ${tracker.total}`}
                  {tracker.percent !== null ? ` · ${tracker.percent}%` : ''}
                </span>
              </div>
              {tracker.percent !== null ? (
                <div className="mt-2">
                  <Bar percent={tracker.percent} color="var(--status-info)" />
                </div>
              ) : null}
              <div className="mt-1.5 text-xs text-fg-muted">
                {tracker.syncedHoursAgo === null
                  ? 'Never counted yet.'
                  : `Counted ${tracker.syncedHoursAgo} hours ago.`}
                {stale ? ' This figure may be behind the spreadsheet.' : ''}
              </div>
            </li>
          )
        })}
      </ul>
    </Section>
  )
}

function DependencyList({
  title,
  items,
  empty,
}: {
  title: string
  items: SharedDependency[]
  empty: string
}) {
  return (
    <div className="flex-1">
      <div className="micro-label text-fg-muted">{title}</div>
      {items.length === 0 ? (
        <p className="mt-1.5 text-sm text-fg-muted">{empty}</p>
      ) : (
        <ul className="card mt-1.5 divide-y divide-border">
          {items.map((item) => (
            <li key={item.name} className="flex flex-wrap items-center gap-2 px-3 py-2">
              <span
                className="h-2 w-2 shrink-0 rounded-full"
                style={{ backgroundColor: ragVar(item.ragStatus) }}
                aria-hidden
              />
              <span className="min-w-0 flex-1 truncate text-sm text-fg">{item.name}</span>
              <span className="text-xs text-fg-muted">{item.percentComplete}%</span>
              <span className="w-24 text-right text-xs text-fg-muted">
                {formatDate(item.targetDate)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function Documents({ page }: { page: SharedProject }) {
  return (
    <ul className="card divide-y divide-border">
      {page.documents.map((document) => (
        <li key={document.slot} className="flex flex-wrap items-center gap-3 px-4 py-2.5">
          <span className="min-w-0 flex-1 text-sm text-fg">{document.label}</span>
          {document.approved ? (
            <Pill tone="green">Signed off</Pill>
          ) : document.present ? (
            <Pill tone="amber">Drafted</Pill>
          ) : (
            <Pill>Not started</Pill>
          )}
          <span className="w-40 text-right text-xs text-fg-muted">
            {document.approved && document.signedBy
              ? `${document.signedBy}, ${formatDate(document.signedAt)}`
              : document.present
                ? `Updated ${formatDate(document.updatedAt)}`
                : ''}
          </span>
        </li>
      ))}
    </ul>
  )
}

function ProjectView({ page }: { page: SharedProject }) {
  const { schedule } = page
  const milestonesComplete = page.milestones.filter((m) => m.isComplete).length

  return (
    <>
      <h1 className="font-heading text-3xl text-fg">{page.name}</h1>
      {page.description ? (
        <p className="mt-2 max-w-[70ch] text-sm text-fg-muted">{page.description}</p>
      ) : null}

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <Pill tone={ragTone(page.ragStatus)}>
          {page.ragStatus === 'none' ? 'No health set' : page.ragStatus}
        </Pill>
        <Pill>{statusLabel(page.status)}</Pill>
        <Pill>{stageLabel(page.stage)}</Pill>
        {schedule.overdueBy ? <Pill tone="red">{schedule.overdueBy} days past target</Pill> : null}
      </div>

      <dl className="mt-4 flex flex-wrap gap-x-8 gap-y-2 text-sm">
        <div>
          <dt className="micro-label text-fg-muted">Owner</dt>
          <dd className="text-fg">{page.owner || 'Not assigned'}</dd>
        </div>
        <div>
          <dt className="micro-label text-fg-muted">Team</dt>
          <dd className="text-fg">{page.teamLabel}</dd>
        </div>
        <div>
          <dt className="micro-label text-fg-muted">Started</dt>
          <dd className="text-fg">{formatDate(page.startDate)}</dd>
        </div>
        <div>
          <dt className="micro-label text-fg-muted">Target</dt>
          <dd className="text-fg">{formatDate(page.targetDate)}</dd>
        </div>
        {page.actualEndDate ? (
          <div>
            <dt className="micro-label text-fg-muted">Finished</dt>
            <dd className="text-fg">{formatDate(page.actualEndDate)}</dd>
          </div>
        ) : null}
      </dl>

      {page.closure ? (
        <div
          className="card mt-6 p-5"
          style={{ borderLeft: '3px solid var(--status-green)' }}
        >
          <div className="micro-label text-fg-muted">Outcome</div>
          <p className="mt-1.5 text-sm text-fg">
            {page.closure.summary ?? 'This project is closed. No outcome summary was recorded.'}
          </p>
          {page.closure.closedOn ? (
            <p className="mt-2 text-xs text-fg-muted">
              Closed {formatDate(page.closure.closedOn)}.
            </p>
          ) : null}
        </div>
      ) : null}

      <Section title="Progress">
        <ProgressAgainstTime page={page} />
      </Section>

      <Section title="At a glance">
        <div className="flex flex-wrap gap-3">
          <Figure
            value={
              schedule.overdueBy !== null
                ? `+${schedule.overdueBy}`
                : schedule.daysRemaining !== null
                  ? String(schedule.daysRemaining)
                  : '—'
            }
            label={schedule.overdueBy !== null ? 'Days past target' : 'Days remaining'}
            tone={schedule.overdueBy !== null ? 'var(--status-red)' : undefined}
          />
          <Figure
            value={
              page.milestones.length ? `${milestonesComplete}/${page.milestones.length}` : '—'
            }
            label="Milestones met"
          />
          <Figure
            value={page.delivery.total ? `${page.delivery.done}/${page.delivery.total}` : '—'}
            label="Work items done"
          />
          <Figure value={String(page.team.length || '—')} label="People assigned" />
        </div>
      </Section>

      <Section title="Milestones">
        <MilestoneList page={page} />
      </Section>

      <Section title="Delivery">
        <Delivery page={page} />
      </Section>

      <Trackers page={page} />

      <Section title="Dependencies">
        <div className="flex flex-wrap gap-6">
          <DependencyList
            title="Waiting on"
            items={page.dependencies.waitingOn}
            empty="Nothing is holding this project up."
          />
          <DependencyList
            title="Blocking"
            items={page.dependencies.blocking}
            empty="No other project is waiting on this one."
          />
        </div>
      </Section>

      <Section title="Team">
        {page.team.length === 0 ? (
          <p className="text-sm text-fg-muted">Nobody is assigned to this project yet.</p>
        ) : (
          <ul className="flex flex-wrap gap-2">
            {page.team.map((person) => (
              <li key={person.name} className="card px-3 py-2">
                <div className="text-sm text-fg">{person.name}</div>
                <div className="micro-label text-fg-muted">
                  {person.isOwner ? 'Owner' : person.role}
                </div>
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Section title="Documents" note="held in SharePoint; ask the owner for access">
        <Documents page={page} />
      </Section>

      <p className="mt-6 text-xs text-fg-muted">
        Last change recorded {new Date(page.updatedAt).toLocaleString()}.
      </p>
    </>
  )
}

function StatusView({ page }: { page: SharedStatus }) {
  // Already sorted by size, and already labelled, by the API.
  const teams = page.byTeam

  return (
    <>
      <h1 className="font-heading text-3xl text-fg">Portfolio status</h1>
      <p className="mt-1 text-sm text-fg-muted">
        As at {new Date(page.generatedAt).toLocaleDateString()} · {page.totals.projects} scheduled
        projects
      </p>

      <div className="mt-6 flex flex-wrap gap-3">
        <Figure value={String(page.totals.projects)} label="Projects" />
        <Figure
          value={String(page.totals.active)}
          label="In flight"
          tone="var(--status-info)"
        />
        <Figure
          value={String(page.totals.atRisk)}
          label="Need attention"
          tone="var(--status-amber)"
        />
        <Figure
          value={String(page.totals.overdue)}
          label="Past target"
          tone="var(--status-red)"
        />
        <Figure
          value={String(page.totals.complete)}
          label="Complete"
          tone="var(--status-green)"
        />
      </div>

      {teams.length > 1 ? (
        <Section title="By team">
          <ul className="flex flex-wrap gap-2">
            {teams.map((team) => (
              <li key={team.team} className="card px-3 py-2 text-sm text-fg">
                {team.label} <span className="text-fg-muted">· {team.count}</span>
              </li>
            ))}
          </ul>
        </Section>
      ) : null}

      <Section title="Projects" note="soonest target first">
        <ul className="card divide-y divide-border">
          {page.projects.map((project) => (
            <li key={project.name} className="px-4 py-3">
              <div className="flex flex-wrap items-center gap-3">
                <span
                  className="h-2.5 w-2.5 shrink-0 rounded-full"
                  style={{ backgroundColor: ragVar(project.ragStatus) }}
                  aria-hidden
                />
                <span className="min-w-0 flex-1 truncate text-fg">{project.name}</span>
                {project.overdue ? (
                  <Pill tone="red">{project.overdueBy} days past target</Pill>
                ) : null}
                <span className="text-sm text-fg-muted">{project.percentComplete}%</span>
                <span className="w-28 text-right text-sm text-fg-muted">
                  {formatDate(project.targetDate)}
                </span>
              </div>
              <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 pl-[22px] text-xs text-fg-muted">
                <span>{project.owner || 'No owner'}</span>
                <span>{project.teamLabel}</span>
                <span>{stageLabel(project.stage)}</span>
                {project.nextMilestone ? (
                  <span className={project.nextMilestone.isOverdue ? 'text-fg' : undefined}>
                    Next: {project.nextMilestone.title} ·{' '}
                    {formatDate(project.nextMilestone.dueDate)}
                  </span>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      </Section>
    </>
  )
}

export function SharePage() {
  const { token } = useParams<{ token: string }>()
  const { data, isPending, error } = useSharedPage(token)

  if (isPending) {
    return (
      <Frame>
        <Loading label="Opening" />
      </Frame>
    )
  }

  if (error || !data) {
    return (
      <Frame>
        <h1 className="font-heading text-2xl text-fg">This link is no longer available</h1>
        <p className="mt-2 max-w-[52ch] text-sm text-fg-muted">
          It may have expired or been withdrawn. Ask whoever sent it for a new one.
        </p>
      </Frame>
    )
  }

  return (
    <Frame>
      {data.kind === 'status' ? <StatusView page={data} /> : <ProjectView page={data} />}
    </Frame>
  )
}
